import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { animeList } from '@/mocks/anime';

interface SimilarAnimeProps {
  currentAnimeId: number;
}

export default function SimilarAnime({ currentAnimeId }: SimilarAnimeProps) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);

  const similar = animeList.filter((a) => a.id !== currentAnimeId).slice(0, 8);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -320 : 320,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section className="py-10 md:py-14 bg-background-50">
      <div className="px-4 md:px-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
              {t('猜你喜欢')}
            </h2>
            <p className="text-sm text-foreground-500 mt-1">{t('相似风格推荐')}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => scroll('left')}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-500 hover:text-white text-foreground-600 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            <button
              onClick={() => scroll('right')}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-500 hover:text-white text-foreground-600 transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto pb-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {similar.map((anime) => (
            <Link
              key={anime.id}
              to={`/anime/${anime.id}`}
              className="flex-shrink-0 w-[180px] md:w-[220px] group cursor-pointer"
            >
              <div className="relative rounded-lg overflow-hidden aspect-[3/4]">
                <img
                  src={anime.cover}
                  alt={anime.title}
                  title={`${anime.title} - 樱花动漫`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3 pt-10">
                  <h3 className="text-white font-medium text-sm mb-0.5 whitespace-nowrap overflow-hidden text-ellipsis">
                    {anime.title}
                  </h3>
                  <p className="text-white/60 text-xs">★ {anime.rating}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}